// ===================================================================
// Impact — Analytics
// Charts, stats, and breakdowns for damage assessment data
// ===================================================================

import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Title, Text, Paper, SimpleGrid, Group, Badge,
  LoadingOverlay, Button,
} from '../../components/gab/GabComponents';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { format, startOfDay, eachDayOfInterval } from 'date-fns';
import { useImpactStore } from '../../store/impactStore';
import { useAuthStore } from '../../store/authStore';
import { primary } from '../../lib/colors';
import { lookupParcel, getParcelMeta } from '../../lib/parcelDb';
import { loadIAConfig, categoryFromPct } from '../../lib/iaConfig';
import { IconDownload, IconInfoCircle } from '@tabler/icons-react';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement,
  PointElement, ArcElement, Tooltip, Legend, Filler,
);

// ── Types ──────────────────────────────────────────────────

interface EnrichedRow {
  assessment:        Record<string, unknown>;
  assessedValue:     number;
  estimatedLoss:     number;
  damagePct:         number;
  suggestedCategory: string;
  owner:             string;
  matchConfidence:   'exact' | 'fuzzy' | null;
}

// ── Helpers ────────────────────────────────────────────────

const fmt$ = (n: number) => n >= 1_000_000
  ? `$${(n / 1_000_000).toFixed(1)}M`
  : n >= 1_000
    ? `$${(n / 1_000).toFixed(0)}K`
    : `$${n.toFixed(0)}`;

function exportCSV(rows: EnrichedRow[], incidentName: string) {
  const headers = ['Address','City','County','Damage Category','Pre-Disaster Value','Estimated Loss','Damage %','Suggested Category','Owner','Match'];
  const lines = [headers.join(',')];
  for (const r of rows) {
    const a = r.assessment as Record<string, string | number | undefined>;
    lines.push([
      `"${a.address ?? ''}"`,
      `"${a.city ?? ''}"`,
      `"${a.county ?? ''}"`,
      a.damageCategory ?? '',
      r.assessedValue,
      r.estimatedLoss,
      r.damagePct > 0 ? r.damagePct.toFixed(1) + '%' : '',
      r.suggestedCategory,
      `"${r.owner}"`,
      r.matchConfidence ?? 'none',
    ].join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `IA_Damage_Report_${incidentName.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd')}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Color Maps ─────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  affected:     '#22c55e',
  minor:        '#f59e0b',
  major:        '#f97316',
  destroyed:    '#ef4444',
  inaccessible: '#8b5cf6',
  infrastructure: '#3b82f6',
  unknown:      '#94a3b8',
};

const SEVERITY_COLORS: Record<string, string> = {
  '1': '#22c55e',
  '2': '#84cc16',
  '3': '#f59e0b',
  '4': '#f97316',
  '5': '#ef4444',
};

const TYPE_COLORS: Record<string, string> = {
  home:           '#4B3FFF',
  individual:     '#3b82f6',
  public:         '#f59e0b',
  business:       '#10b981',
  infrastructure: '#f97316',
  vehicle:        '#8b5cf6',
};

// ── Damage Type Label Map ────────────────────────────────
const DAMAGE_TYPE_LABELS: Record<string, string> = {
  roof: 'Roof', foundation: 'Foundation', flooding: 'Flooding',
  wind: 'Wind', tree: 'Tree/Debris', fire: 'Fire',
  structural: 'Structural', other: 'Other',
  road: 'Road Damage', bridge: 'Bridge', power: 'Power Lines',
  utility: 'Utility', vehicle: 'Vehicle', erosion: 'Erosion',
  school: 'School', hospital: 'Hospital/Medical',
  government: 'Government Building', community: 'Community Facility',
  'water-system': 'Water/Sewer System', communications: 'Communications',
  park: 'Park/Recreation', debris: 'Debris Field',
};

// ── Component ──────────────────────────────────────────────

export default function ImpactAnalytics() {
  const { assessments: allAssessments, loading: isLoading, fetchAll } = useImpactStore();
  const { activeIncident } = useAuthStore();

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filter by active incident
  const assessments = useMemo(() => {
    if (!activeIncident) return allAssessments;
    return allAssessments.filter(a => String(a.incidentId) === String(activeIncident.id));
  }, [allAssessments, activeIncident]);

  // Compute stats locally from filtered assessments
  const stats = useMemo(() => {
    const byCategory: Record<string, number> = {};
    const bySeverity: Record<string, number> = {};
    const byType: Record<string, number> = {};
    const byDamageType: Record<string, number> = {};
    let totalHomes = 0;
    assessments.forEach(a => {
      const homes = a.homeCount || 1;
      totalHomes += homes;
      if (a.damageCategory) byCategory[a.damageCategory] = (byCategory[a.damageCategory] || 0) + homes;
      if (a.severity) bySeverity[a.severity] = (bySeverity[a.severity] || 0) + homes;
      if (a.assessmentType) byType[a.assessmentType] = (byType[a.assessmentType] || 0) + homes;
      if (a.damageTypes?.length) a.damageTypes.forEach(dt => { byDamageType[dt] = (byDamageType[dt] || 0) + homes; });
    });
    return { total: totalHomes, records: assessments.length, byCategory, bySeverity, byType, byDamageType };
  }, [assessments]);

  // Total photos (loaded separately, will be 0 for now)
  const totalPhotos = useMemo(
    () => assessments.reduce((sum, a) => sum + (a.photos?.length || 0), 0),
    [assessments],
  );

  // Timeline data
  const timelineData = useMemo(() => {
    const dated = assessments
      .filter(a => a.datecreated)
      .map(a => ({ ...a, _date: new Date(a.datecreated) }))
      .filter(a => !isNaN(a._date.getTime()));

    if (dated.length === 0) return null;

    const sorted = [...dated].sort((a, b) => a._date.getTime() - b._date.getTime());
    const start = startOfDay(sorted[0]._date);
    const end = startOfDay(sorted[sorted.length - 1]._date);
    const days = eachDayOfInterval({ start, end });

    const counts = days.map(day => {
      const dayStr = format(day, 'yyyy-MM-dd');
      return dated.filter(a => format(a._date, 'yyyy-MM-dd') === dayStr).length;
    });

    // Cumulative
    const cumulative: number[] = [];
    counts.reduce((acc, val) => { cumulative.push(acc + val); return acc + val; }, 0);

    return {
      labels: days.map(d => format(d, 'MMM d')),
      counts,
      cumulative,
    };
  }, [assessments]);

  // County breakdown
  const countyData = useMemo(() => {
    const map: Record<string, number> = {};
    assessments.forEach(a => {
      const county = a.county || 'Unknown';
      map[county] = (map[county] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20);
  }, [assessments]);

  // Damage type breakdown for horizontal bar
  const damageTypeEntries = useMemo(() => {
    return Object.entries(stats.byDamageType)
      .sort((a, b) => b[1] - a[1]);
  }, [stats.byDamageType]);

  const total = assessments.length;
  const geoCount = assessments.filter(a => a.latitude && a.longitude).length;

  // ── Parcel enrichment ──────────────────────────────────────
  const [hasParcelData, setHasParcelData] = useState(false);
  const [enriched, setEnriched] = useState<EnrichedRow[]>([]);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => {
    getParcelMeta().then(m => setHasParcelData(!!m && m.count > 0)).catch(() => {});
  }, []);

  const runEnrichment = useCallback(async () => {
    if (!hasParcelData || assessments.length === 0) return;
    setEnriching(true);
    const cfg = loadIAConfig();
    const rows: EnrichedRow[] = [];
    for (const a of assessments) {
      if (!a.address) continue;
      const match = await lookupParcel(a.address, a.city);
      const assessedValue = match?.parcel.assessedValue ?? 0;
      const estimatedLoss = a.estimatedLoss ?? 0;
      const damagePct = assessedValue > 0 ? (estimatedLoss / assessedValue) * 100 : 0;
      rows.push({
        assessment:    a,
        assessedValue,
        estimatedLoss,
        damagePct,
        suggestedCategory: assessedValue > 0 ? categoryFromPct(damagePct, cfg) : (a.damageCategory ?? ''),
        owner:         match?.parcel.owner ?? '',
        matchConfidence: match?.confidence ?? null,
      });
    }
    setEnriched(rows);
    setEnriching(false);
  }, [hasParcelData, assessments]);

  useEffect(() => { runEnrichment(); }, [runEnrichment]);

  const propertyStats = useMemo(() => {
    if (enriched.length === 0) return null;
    const matched = enriched.filter(r => r.assessedValue > 0);
    const totalAssessed = matched.reduce((s, r) => s + r.assessedValue, 0);
    const totalLoss     = matched.reduce((s, r) => s + r.estimatedLoss, 0);
    const avgDamagePct  = matched.length > 0
      ? matched.reduce((s, r) => s + r.damagePct, 0) / matched.length
      : 0;

    // Breakdown by category
    const byCategory: Record<string, { count: number; assessed: number; loss: number; pcts: number[] }> = {};
    for (const r of matched) {
      const cat = r.assessment.damageCategory || r.suggestedCategory || 'unknown';
      if (!byCategory[cat]) byCategory[cat] = { count: 0, assessed: 0, loss: 0, pcts: [] };
      byCategory[cat].count++;
      byCategory[cat].assessed += r.assessedValue;
      byCategory[cat].loss     += r.estimatedLoss;
      byCategory[cat].pcts.push(r.damagePct);
    }

    return { totalAssessed, totalLoss, avgDamagePct, matchRate: matched.length / enriched.length, byCategory, matchedCount: matched.length };
  }, [enriched]);

  if (total === 0 && !isLoading) {
    return (
      <div>
        <Group gap="xs" mb="md">
          <Title order={3} c="black">Analytics</Title>
        </Group>
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No assessments to analyze. Create some assessments first.</Text>
        </Paper>
      </div>
    );
  }

  // ── Chart Configs ──────────────────────────────────────

  const categoryChartData = {
    labels: Object.keys(stats.byCategory).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [{
      label: 'Assessments',
      data: Object.values(stats.byCategory),
      backgroundColor: Object.keys(stats.byCategory).map(k => CATEGORY_COLORS[k] || '#6b7280'),
      borderRadius: 4,
    }],
  };

  const typeChartData = {
    labels: Object.keys(stats.byType).map(k => k.charAt(0).toUpperCase() + k.slice(1)),
    datasets: [{
      data: Object.values(stats.byType),
      backgroundColor: Object.keys(stats.byType).map(k => TYPE_COLORS[k] || '#6b7280'),
      borderWidth: 0,
    }],
  };

  const severityChartData = {
    labels: Object.keys(stats.bySeverity).map(k => `Severity ${k}`),
    datasets: [{
      label: 'Assessments',
      data: Object.values(stats.bySeverity),
      backgroundColor: Object.keys(stats.bySeverity).map(k => SEVERITY_COLORS[k] || '#6b7280'),
      borderRadius: 4,
    }],
  };

  const VIZ_PALETTE = ['#4B3FFF','#3b82f6','#10b981','#f59e0b','#f97316','#ef4444','#8b5cf6','#ec4899','#06b6d4','#84cc16'];

  const damageTypeChartData = {
    labels: damageTypeEntries.map(([k]) => DAMAGE_TYPE_LABELS[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())),
    datasets: [{
      label: 'Count',
      data: damageTypeEntries.map(([, v]) => v),
      backgroundColor: damageTypeEntries.map((_, i) => VIZ_PALETTE[i % VIZ_PALETTE.length]),
      borderRadius: 4,
    }],
  };

  const timelineChartData = timelineData ? {
    labels: timelineData.labels,
    datasets: [
      {
        label: 'Daily',
        data: timelineData.counts,
        borderColor: primary[700],
        backgroundColor: 'rgba(75, 63, 255, 0.1)',
        fill: true,
        tension: 0.3,
        pointRadius: 2,
      },
      {
        label: 'Cumulative',
        data: timelineData.cumulative,
        borderColor: 'var(--data-viz-350)',
        backgroundColor: 'transparent',
        borderDash: [4, 4],
        tension: 0.3,
        pointRadius: 0,
        yAxisID: 'y1',
      },
    ],
  } : null;

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
      x: { ticks: { font: { size: 11 } } },
    },
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: { legend: { display: false } },
    scales: {
      x: { beginAtZero: true, ticks: { stepSize: 1, font: { size: 11 } } },
      y: { ticks: { font: { size: 11 } } },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' as const, labels: { padding: 16, usePointStyle: true } } },
  };

  const timelineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' as const, labels: { usePointStyle: true } } },
    scales: {
      y: { beginAtZero: true, title: { display: true, text: 'Daily' } },
      y1: { position: 'right' as const, beginAtZero: true, title: { display: true, text: 'Cumulative' }, grid: { drawOnChartArea: false } },
    },
  };

  return (
    <div style={{ position: 'relative' }}>
      <LoadingOverlay visible={isLoading && assessments.length === 0} />

      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <Title order={3} c="black">Analytics</Title>
          {activeIncident && (
            <Badge size="sm" variant="light" color="var(--color-primary-700)">{activeIncident.name}</Badge>
          )}
        </Group>
      </Group>

      {/* Key Metrics Row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md" spacing="sm">
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-800)' }}>
          <Text size="xs" c="dimmed">Total Homes/Units</Text>
          <Text fz={28} fw={700}>{stats.total}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-primary-900)' }}>
          <Text size="xs" c="dimmed">Assessment Records</Text>
          <Text fz={28} fw={700}>{stats.records}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text size="xs" c="dimmed">Geolocated</Text>
          <Text fz={28} fw={700} c={primary[700]}>{geoCount}</Text>
        </Paper>
        <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--data-viz-350)' }}>
          <Text size="xs" c="dimmed">Photos</Text>
          <Text fz={28} fw={700} c="var(--data-viz-350)">{totalPhotos}</Text>
        </Paper>
      </SimpleGrid>

      {/* Charts Row 1: Category Bar + Type Doughnut */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Damage Category</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            <Bar data={categoryChartData} options={chartOptions} />
          </div>
        </Paper>
        <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Assessment Type</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            <Doughnut data={typeChartData} options={doughnutOptions} />
          </div>
        </Paper>
      </SimpleGrid>

      {/* Timeline Chart */}
      {timelineChartData && (
        <Paper p="md" radius="sm" withBorder mb="md" style={{ display: 'flex', flexDirection: 'column' }}>
          <Text fw={600} size="sm" mb="sm">Assessment Timeline</Text>
          <div style={{ flex: 1, minHeight: 220 }}>
            <Line data={timelineChartData} options={timelineOptions} />
          </div>
        </Paper>
      )}

      {/* Charts Row 2: Damage Type Horizontal Bar + Severity Bar */}
      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {damageTypeEntries.length > 0 && (
          <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
            <Text fw={600} size="sm" mb="sm">Damage Types</Text>
            <div style={{ flex: 1, minHeight: 200, overflowY: 'auto' }}>
              <div style={{ height: Math.max(200, damageTypeEntries.length * 28) }}>
                <Bar data={damageTypeChartData} options={horizontalBarOptions} />
              </div>
            </div>
          </Paper>
        )}
        {Object.keys(stats.bySeverity).length > 0 && (
          <Paper p="md" radius="sm" withBorder style={{ display: 'flex', flexDirection: 'column' }}>
            <Text fw={600} size="sm" mb="sm">Severity Distribution</Text>
            <div style={{ flex: 1, minHeight: 220 }}>
              <Bar data={severityChartData} options={chartOptions} />
            </div>
          </Paper>
        )}
      </SimpleGrid>

      {/* ── Property Value Exposure ── */}
      {hasParcelData && (
        <>
          {/* Stat cards */}
          <Paper p="md" radius="sm" withBorder mb="md">
            <Group justify="space-between" mb="sm">
              <Text fw={600} size="sm">Property Value Exposure</Text>
              <Group gap="xs">
                {propertyStats && (
                  <Text size="xs" c="dimmed">
                    {propertyStats.matchedCount} of {enriched.length} matched ({Math.round(propertyStats.matchRate * 100)}%)
                  </Text>
                )}
                <Button
                  size="xs"
                  variant="outline"
                  color="var(--color-primary-700)"
                  loading={enriching}
                  onClick={() => exportCSV(enriched, activeIncident?.name ?? 'incident')}
                  style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <IconDownload size={12} /> Export Report
                </Button>
              </Group>
            </Group>

            {enriching && (
              <Text size="sm" c="dimmed">Matching assessments to parcel data…</Text>
            )}

            {!enriching && !propertyStats && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#94a3b8', fontSize: 13 }}>
                <IconInfoCircle size={14} />
                No assessments with addresses matched to parcel data. Ensure assessments include street addresses.
              </div>
            )}

            {!enriching && propertyStats && (
              <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm" mb="md">
                <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #4B3FFF' }}>
                  <Text size="xs" c="dimmed">Total Assessed Value</Text>
                  <Text fz={24} fw={700} c="#4B3FFF">{fmt$(propertyStats.totalAssessed)}</Text>
                </Paper>
                <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-error)' }}>
                  <Text size="xs" c="dimmed">Total Estimated Loss</Text>
                  <Text fz={24} fw={700} c="var(--color-error)">{fmt$(propertyStats.totalLoss)}</Text>
                </Paper>
                <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid #d97706' }}>
                  <Text size="xs" c="dimmed">Avg Damage %</Text>
                  <Text fz={24} fw={700} c="#d97706">{propertyStats.avgDamagePct.toFixed(1)}%</Text>
                </Paper>
                <Paper p="sm" radius="sm" withBorder ta="center" style={{ borderTop: '3px solid var(--color-success)' }}>
                  <Text size="xs" c="dimmed">Loss / Assessed</Text>
                  <Text fz={24} fw={700} c="var(--color-success)">
                    {propertyStats.totalAssessed > 0
                      ? ((propertyStats.totalLoss / propertyStats.totalAssessed) * 100).toFixed(1) + '%'
                      : '—'}
                  </Text>
                </Paper>
              </SimpleGrid>
            )}

            {/* Category breakdown table */}
            {!enriching && propertyStats && Object.keys(propertyStats.byCategory).length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Properties</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Assessed Value</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Estimated Loss</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600 }}>Avg Damage %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {['destroyed','major','minor','affected','inaccessible','unknown'].map(cat => {
                      const d = propertyStats.byCategory[cat];
                      if (!d) return null;
                      const avgPct = d.pcts.length > 0 ? d.pcts.reduce((a, b) => a + b, 0) / d.pcts.length : 0;
                      const color = { destroyed: '#dc2626', major: '#ea580c', minor: '#d97706', affected: '#16a34a', inaccessible: '#6b7280' }[cat] ?? '#6b7280';
                      return (
                        <tr key={cat} style={{ borderBottom: '1px solid #f1f5f9' }}>
                          <td style={{ padding: '8px 12px' }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </span>
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.count}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{fmt$(d.assessed)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{fmt$(d.loss)}</td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>{d.assessed > 0 ? avgPct.toFixed(1) + '%' : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '2px solid #e2e8f0', fontWeight: 700, background: '#f8fafc' }}>
                      <td style={{ padding: '8px 12px' }}>Total</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{propertyStats.matchedCount}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{fmt$(propertyStats.totalAssessed)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{fmt$(propertyStats.totalLoss)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{propertyStats.avgDamagePct.toFixed(1)}%</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Paper>
        </>
      )}

      {/* County Breakdown Table */}
      {countyData.length > 0 && (
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Assessments by County</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'left' }}>County</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>Count</th>
                  <th style={{ padding: '8px 16px', textAlign: 'center' }}>% of Total</th>
                </tr>
              </thead>
              <tbody>
                {countyData.map(([county, count]) => (
                  <tr key={county} style={{ borderBottom: '1px solid var(--frost)' }}>
                    <td style={{ padding: '8px 16px' }}>{county}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>{count}</td>
                    <td style={{ padding: '8px 16px', textAlign: 'center' }}>
                      {total > 0 ? Math.round((count / total) * 100) : 0}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
